import type { ImageSources, SrcSetSizes } from '@snapchat/mw-contentful-client';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import type { CarouselV3AspectRatios } from '@snapchat/snap-design-system-marketing';
import {
  CarouselCardItem as CarouselCardItemSDS,
  mobileMaxWidth,
} from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { shouldFlushLogs } from '../../helpers/getLocalPath';
import { logger, logUserEvent } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import { combineImageSources } from '../../utils/combineImageSources';
import { parseMedia } from '../../utils/media';
import { imageQuality } from '../Image';
import type { ImageDataProps } from '../Image/types';
import type { CarouselV3CardItemProps } from './types';

export const getPortraitCardImgSrcSetSizes = (quality: number): SrcSetSizes => ({
  sizeToUrl: [
    {
      size: '600w',
      settings: { width: 600, quality },
    },
  ],
  sizes: '600px',
});

const cardWidths = [622, 728];
const cardWidthsStr = cardWidths.map(cw => `${cw}px`).join(', ');

export const getLandscapeCardImgSrcSetSizes = (quality: number): SrcSetSizes => ({
  sizeToUrl: cardWidths.map(width => ({
    size: `${width}w`,
    settings: { width, quality },
  })),
  sizes: `(max-width: ${mobileMaxWidth}px) ${cardWidthsStr}`,
});

export const CarouselV3ImageItem: FC<{
  cardItem: CarouselV3CardItemProps;
  mediaAspectRatio: CarouselV3AspectRatios;
  isVisible?: boolean;
  shouldLoad?: boolean;
  isResponsiveWidth?: boolean;
  hideLinkArrow?: boolean;
  className?: string;
}> = ({
  cardItem,
  mediaAspectRatio,
  isVisible,
  shouldLoad,
  isResponsiveWidth,
  hideLinkArrow,
  ...otherProps
}) => {
  const imageMedia = cardItem.media as ImageDataProps;

  const { imageSource, imageAltText } = parseMedia(imageMedia.media);
  const { imageSource: mobileImageSource } = parseMedia(imageMedia.mobileMedia);
  const { getImageSources } = useContentfulImages();

  const elementLocation = useContext(ElementLocation).elementLocation;

  const quality = imageQuality[imageMedia.quality ?? 'Standard'];
  const imgSettings =
    mediaAspectRatio === '9:16'
      ? getPortraitCardImgSrcSetSizes(quality)
      : getLandscapeCardImgSrcSetSizes(quality);

  const imageSrcSettings = { size: imgSettings, quality };

  const imgSrcs = combineImageSources({
    desktop: getImageSources(imageSource, imageSrcSettings),
    /* TODO(kpan2): These aren't good for mobile images. */
    mobile: getImageSources(mobileImageSource, imageSrcSettings),
  });

  // Logo settings
  const logoMedia = cardItem.logo as ImageDataProps | undefined;
  let logoImgSrcs: ImageSources | undefined = undefined;
  let logoAltText: string | undefined = undefined;

  if (logoMedia) {
    const { imageSource, imageAltText } = parseMedia(logoMedia.media);
    const { imageSource: mobileImageSource } = parseMedia(logoMedia.mobileMedia);
    const quality = imageQuality[logoMedia.quality ?? 'Standard'];
    const size =
      mediaAspectRatio === '9:16'
        ? getPortraitCardImgSrcSetSizes(quality)
        : getLandscapeCardImgSrcSetSizes(quality);

    logoImgSrcs = combineImageSources({
      desktop: getImageSources(imageSource, { size, quality }),
      mobile: getImageSources(mobileImageSource, { size, quality }),
    });

    logoAltText = imageAltText;
  }

  const slug = cardItem.slugReference?.slug;
  const url = slug ? `/${slug}` : cardItem.url;

  const onClick = (): void => {
    if (!url) {
      return;
    }

    logUserEvent({
      eventAction: UserAction.Click,
      eventCategory: 'CarouselV3',
      eventLabel: `CarouselV3.ImageItem - ${url}`,
      context: { targetUrl: url, elementLocation },
    });

    if (shouldFlushLogs(url)) {
      void logger.flush();
    }
  };

  return (
    <CarouselCardItemSDS
      aspectRatio={mediaAspectRatio}
      body={cardItem.body}
      key={cardItem.sys.id}
      subtitle={cardItem.subtitle}
      title={cardItem.title}
      url={url}
      imgSrcs={imgSrcs}
      imgAltText={imageAltText}
      isVisible={isVisible}
      shouldLoad={shouldLoad}
      onClick={onClick}
      isResponsiveWidth={isResponsiveWidth}
      hideLinkArrow={hideLinkArrow}
      logoSrcs={logoImgSrcs}
      logoAltText={logoAltText}
      {...otherProps}
    />
  );
};

CarouselV3ImageItem.displayName = 'CarouselV3ImageItem';
